// src/components/Modules/Vehicles/VehicleTransfersModal.js
import React, { useState, useEffect, useCallback } from "react";
import api from "../../../axiosConfig";
import {
  Modal,
  Card,
  Tag,
  Spin,
  Typography,
  Button,
  Row,
  Col,
  DatePicker,
  Alert,
  Empty,
  Divider,
  Space,
  Badge,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  ClockCircleOutlined,
  UserOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  MedicineBoxOutlined,
  InfoCircleOutlined,
  CarFilled,
  InfoOutlined,
  PhoneOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { formatCurrencyCOP } from "../../../utils/formatCurrency";
import ObservacionesDialog from "../Dialog/ObservacionesDialog"; // Importar el diálogo

const { Text } = Typography;

const VehicleTransfersModal = ({ vehicleId, open, onClose }) => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dateText, setDateText] = useState("");
  const [vehicleInfo, setVehicleInfo] = useState({ codigo: "", placa: "" });
  const [observacionesDialogOpen, setObservacionesDialogOpen] = useState(false);
  const [selectedTransferId, setSelectedTransferId] = useState(null);

  const handleOpenObservaciones = (transferId) => {
    setSelectedTransferId(transferId);
    setObservacionesDialogOpen(true);
  };

  const handleCloseObservacionesDialog = () => {
    setObservacionesDialogOpen(false);
    setSelectedTransferId(null);
  }

  useEffect(() => {
    const today = dayjs();
    if (selectedDate.isSame(today, "day")) {
      setDateText("Hoy");
    } else {
      setDateText(selectedDate.locale("es").format("dddd, D [de] MMMM"));
    }
  }, [selectedDate]);

  const fetchVehicleInfo = useCallback (async () => {
    try {
      const response = await api.get(`/vehiculos/${vehicleId}`);
      setVehicleInfo({
        codigo: response.data.codigo,
        placa: response.data.placa,
      });
    } catch (err) {
      console.error("Error cargando información del vehículo:", err);
    }
  }, [vehicleId]);

  const fetchTransfers = useCallback (async (fecha) => {
    if (!vehicleId) {
      setError("No se ha seleccionado un vehículo válido");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/traslados/vehiculo/${vehicleId}`, {
        params: {
          fecha: fecha.format("YYYY-MM-DD"),
          _t: Date.now(),
        },
      });

      setTransfers(response.data.traslados || []);
    } catch (err) {
      console.error(
        "Error cargando traslados:",
        err.response?.data || err.message
      );
      setError(err.response?.data?.error || "Error al cargar traslados");
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    if (open && vehicleId) {
      const today = dayjs();
      setSelectedDate(today);
      fetchVehicleInfo();
      fetchTransfers(today);
    } else {
      setTransfers([]);
      setError(null);
    }
  }, [open, vehicleId, fetchTransfers, fetchVehicleInfo]);

  const navigateDays = (days) => {
    const newDate = selectedDate.clone().add(days, "days");
    setSelectedDate(newDate);
    fetchTransfers(newDate);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      Cancelado: "#b10e1a",
      Finalizado: "#4d4d4d",
      Fallido: "#4a2c04",
      FallidoXvalidar: "#d4380d",
      FinalizadoXvalidar: "#722ED1",
      Asignado: "#1f7362",
      "Pre-Asignado": "#0c27a0",
      Programado: "#6c06b5",
      Enterado: "#0c5b07",
      Iniciado: "#fa7114",
      Aceptado: "#088A08",
      "En Camino": "#FFBF00",
      Esperando: "#a44706",
      default: "#b0b500",
    };
    return statusColors[status] || statusColors.default;
  };

  const getConfirmacionColor = (confirmacion) => {
    return confirmacion === "Confirmado" ? "#52c41a" : "#f5222d";
  };

  const getInicioStatusColor = (inicioStatus) => {
    const inicioStatusColors = {
      "Inicio Exitosamente": "#52c41a",
      "Ubicación No Válida para Iniciar": "#faad14",
      "Sin Conectividad Para Iniciar": "#faad14",
    };
    return inicioStatusColors[inicioStatus] || "#faad14";
  };

  const getFinStatusColor = (finStatus) => {
    const finStatusColors = {
      "Finalizó Exitosamente": "#52c41a",
      "Ubicación No Válida para Finalizar": "#faad14",
      "Sin Conectividad Para Finalizar": "#faad14",
    };
    return finStatusColors[finStatus] || "#faad14";
  };

  const getSeguimientoColor = (seguimiento) => {
    return seguimiento === "Validado" ? "#52c41a" : "#f5222d";
  };

  const renderTransferCard = (transfer) => {
    const statusColor = getStatusColor(transfer.status);
    const confirmacionColor = getConfirmacionColor(transfer.confirmacion);
    const inicioStatusColor = getInicioStatusColor(transfer.inicio_status);
    const finStatusColor = getFinStatusColor(transfer.fin_status);
    const seguimientoColor = getSeguimientoColor(transfer.seguimiento);

    return (
      <Card
        key={transfer.id}
        style={{ 
          marginBottom: "16px",
          borderRadius: "8px",
          borderLeft: `4px solid ${statusColor}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.09)",
          padding: 0
        }}
      >
        {/* Encabezado con ID y estados */}
        <div
          style={{
            backgroundColor: "#0a2e5c",
            padding: "10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            flexWrap: "wrap",
          }}
        >
          <Space>
            <Text
              strong
              style={{
                color: "#fff",
                marginRight: "8px",
                wordBreak: "break-all",
              }}
            >
              CS-{transfer.id}
            </Text>
          </Space>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <Tag color={statusColor} style={{ fontWeight: 500 }}>
              {transfer.status}
            </Tag>

            <Tag
              color={confirmacionColor}
              style={{ fontWeight: 500 }}
            >
              {transfer.confirmacion === "Confirmado" ? "Confirmado" : "Sin Confirmar"}
            </Tag>

            {transfer.inicio_status && (
              <Tag color={inicioStatusColor} style={{ fontWeight: 500 }}>
                {transfer.inicio_status}
              </Tag>
            )}

            {transfer.fin_status && (
              <Tag color={finStatusColor} style={{ fontWeight: 500 }}>
                {transfer.fin_status}
              </Tag>
            )}

            <Tag
              color={seguimientoColor}
              style={{ fontWeight: 500 }}
            >
              {transfer.seguimiento === "Validado" ? "Validado" : "Sin Validar"}
            </Tag>
          </div>
        </div>

        {/* Contenido principal - Distribución de 3 columnas */}
        <Row gutter={[16, 16]} style={{ padding: "10px" }}>
          {/* Columna izquierda: Fechas y horas */}
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" size="small">
              <Space>
                <ClockCircleOutlined style={{ color: "#1890ff" }} />
                <Text>{dayjs(transfer.fecha).format("DD/MM/YYYY")}</Text>
              </Space>
              <Space>
                <ClockCircleOutlined style={{ color: "#1890ff" }} />
                <Text>Recogida: {transfer.hora_recogida?.slice(0, 5) || "N/A"}</Text>
              </Space>
              <Space>
                <ClockCircleOutlined style={{ color: "#1890ff" }} />
                <Text>Llegada: {transfer.hora_llegada?.slice(0, 5) || "N/A"}</Text>
              </Space>
            </Space>
          </Col>

          {/* Columna central: Origen y destino */}
          <Col xs={24} sm={12} md={8} style={{ textAlign: "center" }}>
            <Space direction="vertical" size="small">
              <Space>
                <EnvironmentOutlined
                  style={{
                    color: transfer.origen_lugar ? "#52c41a" : "#cccccc",
                  }}
                />
                <Text>
                  <span style={{ fontWeight: "bold" }}>
                    {transfer.origen_lugar || "Origen"}
                  </span><br />
                  {transfer.origen_direccion} - {transfer.origen_ciudad}
                </Text>
              </Space>
              <Space>
                <EnvironmentOutlined
                  style={{
                    color: transfer.destino_lugar ? "#f5222d" : "#cccccc",
                  }}
                />
                <Text>
                  <span style={{ fontWeight: "bold" }}>
                    {transfer.destino_lugar || "Destino"}
                  </span><br />
                  {transfer.destino_direccion} - {transfer.destino_ciudad}
                </Text>
              </Space>
            </Space>
          </Col>

          {/* Columna derecha: Información del paciente */}
          <Col xs={24} sm={24} md={8} style={{ textAlign: "right" }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center',
                marginBottom: 4,
                gap: 8
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <UserOutlined style={{ color: "#1890ff", marginRight: 8 }} />
                  <Text>{transfer.nombre_paciente}</Text>
                </div>
                
                {transfer.observaciones_count > 0 && (
                  <Badge
                      count={transfer.observaciones_count}
                      style={{ 
                        backgroundColor: "#52c41a",
                        cursor: "pointer",
                        transform: 'scale(0.8)',
                        transformOrigin: 'center',
                        position: 'relative',
                        top: '-10px',    
                        right: '10px',  
                        zIndex: 1        
                      }}
                      onClick={() => handleOpenObservaciones(transfer.id)}
                    >
                      <CommentOutlined 
                        style={{ 
                          color: "#1890ff",
                          fontSize: "20px",  
                          position: 'relative',
                          zIndex: 0
                        }}
                      />
                    </Badge>
                )}
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Text style={{ color: "#cccccc", marginRight: 4 }}>
                  {transfer.tipo_documento_paciente}
                </Text>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <IdcardOutlined style={{ color: "#1890ff", marginRight: 8 }} />
                <Text>{transfer.numero_documento_paciente}</Text>
              </div>
              
              {transfer.numero_contacto_paciente && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <PhoneOutlined style={{ color: "#1890ff", marginRight: 8 }} />
                  <Text>{transfer.numero_contacto_paciente}</Text>
                </div>
              )}
            </Space>
          </Col>
        </Row>

        {/* Empresa y Copago */}
        <Row gutter={[16, 16]} style={{ padding: "0 10px" }}>
          <Col xs={24} sm={12} md={12} style={{ display: "flex", alignItems: "center" }}>
            <MedicineBoxOutlined style={{ color: "#1890ff", marginRight: 8 }} />
            <Text>{transfer.empresa}</Text>
          </Col>
          <Col xs={24} sm={12} md={12} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            <DollarOutlined style={{ color: "#1890ff", marginRight: 8 }} />
            <Text>{formatCurrencyCOP(transfer.copago_diario) || "$ 0,00"}</Text>
          </Col>
        </Row>

        {/* Autorizaciones */}
        {(transfer.autorizacion || transfer.preautorizacion) && (
          <Row 
            justify="center" 
            gutter={[16, 16]} 
            style={{ 
              marginTop: "10px", 
              textAlign: "center",
              padding: "0 15px"
            }}
          >
            {transfer.autorizacion && (
              <Col xs={24} sm={12} md={12}>
                <Space>
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                  <Text>
                    <strong>Autorización:</strong> {transfer.autorizacion}
                  </Text>
                </Space>
              </Col>
            )}
            {transfer.preautorizacion && (
              <Col xs={24} sm={12} md={12}>
                <Space>
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                  <Text>
                    <strong>Preautorización:</strong> {transfer.preautorizacion}
                  </Text>
                </Space>
              </Col>
            )}
          </Row>
        )}

        {/* Observaciones e indicaciones */}
        {transfer.observaciones_traslado && (
          <Row gutter={[16, 16]} style={{ padding: "0 15px", marginTop: 8 }}>
            <Col span={24}>
              <Space>
                <InfoCircleOutlined style={{ color: "#faad14" }} />
                <Text style={{ color: "orange" }}>
                  {transfer.observaciones_traslado}
                </Text>
              </Space>
            </Col>
          </Row>
        )}

        {transfer.indicaciones_conductor && (
          <Row gutter={[16, 16]} style={{ padding: "0 15px", marginTop: 8 }}>
            <Col span={24}>
              <Space>
                <InfoOutlined style={{ color: "#faad14" }} />
                <Text style={{ color: "orange" }}>
                  {transfer.indicaciones_conductor}
                </Text>
              </Space>
            </Col>
          </Row>
        )}

        {/* Pie de tarjeta */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          padding: "10px",
          flexWrap: "wrap",
          marginTop: 8
        }}>
          <Text>
            {transfer.tipo_traslado} - {transfer.tipo_viaje}
          </Text>
          {transfer.placa && (
            <Text>
              <CarFilled style={{ color: "#1890ff", marginRight: 8 }} />
              {transfer.nombre_vehiculo ? `${transfer.nombre_vehiculo} - ` : ''}{transfer.placa}
            </Text>
          )}
        </div>
      </Card>
    );
  };

  return (
    <Modal
      title={
        <div>
          <span>Historial de Traslados</span>
          {vehicleInfo.codigo && (
            <Text strong style={{ marginLeft: 8 }}>
              {vehicleInfo.codigo} / Placa: {vehicleInfo.placa}
            </Text>
          )}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={1500}
      centered
      style={{ top: 20 }}
    >
      {/* Selector de fecha */}
      <Row justify="center" align="middle" style={{ marginBottom: 20 }}>
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigateDays(-1)}
          style={{ width: 40, height: 40 }}
        />

        <DatePicker
          value={selectedDate}
          onChange={(date) => {
            if (date) {
              setSelectedDate(date);
              fetchTransfers(date);
            }
          }}
          format="DD/MM/YYYY"
          placeholder="Seleccionar fecha"
          size="large"
          allowClear={false}
          style={{ width: 200, margin: "0 10px" }}
          getPopupContainer={(trigger) => trigger.parentNode}
          inputReadOnly={false}
          disabledDate={() => false}
        />

        <Button
          icon={<RightOutlined />}
          onClick={() => navigateDays(1)}
          style={{ width: 40, height: 40 }}
        />
      </Row>

      <Divider style={{ margin: "10px 0" }}>
        <Text strong style={{ fontSize: 16 }}>
          {dateText}
        </Text>
      </Divider>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading} tip="Cargando traslados..." delay={300}>
        {transfers.length > 0 ? (
          <div
            style={{ maxHeight: "60vh", overflowY: "auto", padding: "0 5px" }}
          >
            {transfers.map(renderTransferCard)}
          </div>
        ) : !loading && !error ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                No se encontraron traslados para este vehículo el {dateText}
              </span>
            }
            style={{ margin: "40px 0" }}
          />
        ) : null}
      </Spin>
      {observacionesDialogOpen && selectedTransferId && (
        <ObservacionesDialog
          open={observacionesDialogOpen}
          onClose={handleCloseObservacionesDialog}
          trasladoId={selectedTransferId.toString()}
        />
      )}
    </Modal>
  );
};

export default VehicleTransfersModal;